// Job photo UI — strip (compact, JobDrawer) and grid (full, JobDetail)
// share the same internals: thumbnail rendering, signed-URL caching,
// upload pipeline, lightbox.
//
// Signed-URL caching: each photo path is keyed in React Query with a
// 290s staleTime — 10s below the 300s TTL the helper requests, so a
// rendered thumbnail never points at a URL closer than 10s from
// expiration. On re-observation past staleTime, React Query refetches
// transparently. The same cache is shared between the thumbnail render
// and the lightbox open — the lightbox doesn't pay a second round-trip
// for the cached URL.
//
// Upload pipeline: per-file in-flight slot tracked in component state
// so the strip shows skeleton placeholders during upload. On success,
// invalidate the photos query (the real thumbnail replaces the
// skeleton); on failure, drop the slot and toast. All bucket/path
// logic lives in src/lib/jobPhotos.ts — this component never touches
// supabase.storage directly.
//
// Delete permission: matches the NoteThread.tsx convention — own
// uploads OR isOwnerOrAdmin. Even though the table-level RLS would let
// any workspace member delete (the policy is workspace-scoped, not
// uploader-scoped), gating at the UI layer matches how the rest of the
// app handles "content authored by a teammate."
//
// HEIC caveat: HEIC is allowed by the bucket + helper mime list so iOS
// devices can upload (the iOS picker may deliver HEIC if the user picks
// the option not to convert). Chrome/Firefox cannot render HEIC; the
// thumbnail will show as a broken image on those browsers. Safari and
// iOS WebKit render HEIC natively. A v2 follow-up could transcode on
// upload (edge function w/ libheif) or strip HEIC from the input
// accept list and force conversion at the picker.

import { useEffect, useMemo, useRef, useState, type TouchEvent as ReactTouchEvent } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Image as ImageIcon, Plus, X, Trash2, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react'
import { useAuth } from '@/lib/auth'
import { supabase } from '@/lib/supabase'
import {
  listJobPhotos, uploadJobPhoto, deleteJobPhoto,
  getJobPhotoSignedUrl,
  PHOTO_ACCEPT_ATTR,
  type JobPhoto,
} from '@/lib/jobPhotos'
import { transcodeImageToWebP } from '@/lib/imageTranscode'

const ACCEPT = PHOTO_ACCEPT_ATTR

interface BaseProps {
  jobId: string
  workspaceId: string
}

// ─── Thumb (used by both layouts) ────────────────────────────────────────────

// queryKeyPrefix lets the exported Lightbox below share the caller's
// thumbnail cache. Default 'job-photo-url' preserves the staff
// JobPhotoStrip behavior; vendor/portal callers pass their own key
// (their thumbnail useQuery uses the same prefix) so opening the
// lightbox is a cache hit, not a fresh fetch.
function useSignedThumbUrl(storagePath: string, queryKeyPrefix: string = 'job-photo-url') {
  return useQuery({
    queryKey: [queryKeyPrefix, storagePath],
    queryFn: async () => {
      const { url, error } = await getJobPhotoSignedUrl(storagePath, 300)
      if (error || !url) throw error ?? new Error('No signed URL')
      return url
    },
    staleTime: 290_000,
    gcTime: 290_000,
    retry: 1,
  })
}

function Thumbnail({ photo, size, onClick }: { photo: JobPhoto; size: number; onClick: () => void }) {
  const { data: url, isLoading } = useSignedThumbUrl(photo.storage_path)
  return (
    <button type="button" onClick={onClick}
      aria-label="Open photo"
      style={{
        width: size, height: size, padding: 0,
        background: 'var(--hover-bg)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 8, cursor: 'pointer', overflow: 'hidden',
        flexShrink: 0, position: 'relative',
      }}>
      {url && !isLoading ? (
        <img src={url} alt={photo.caption || ''}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          loading="lazy" />
      ) : (
        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Loader2 style={{ width: 14, height: 14, color: 'var(--ink-tertiary)', animation: 'spin 1s linear infinite' }} />
        </div>
      )}
    </button>
  )
}

function SkeletonSlot({ size }: { size: number }) {
  return (
    <div aria-label="Uploading photo"
      style={{
        width: size, height: size, flexShrink: 0,
        background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)',
        borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
      <Loader2 style={{ width: 14, height: 14, color: 'var(--ink-tertiary)', animation: 'spin 1s linear infinite' }} />
    </div>
  )
}

// ─── Upload trigger (shared) ─────────────────────────────────────────────────

function AddPhotoButton({
  size, onFiles, layout,
}: { size: number; onFiles: (files: FileList) => void; layout: 'square' | 'inline' }) {
  const inputRef = useRef<HTMLInputElement>(null)
  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        multiple
        onChange={e => { if (e.target.files?.length) { onFiles(e.target.files); e.target.value = '' } }}
        style={{ display: 'none' }}
      />
      <button type="button"
        onClick={() => inputRef.current?.click()}
        aria-label="Add photos"
        style={layout === 'square' ? {
          width: size, height: size, flexShrink: 0,
          background: 'var(--accent-dim)', border: '1px dashed var(--accent-border)',
          borderRadius: 8, cursor: 'pointer', color: 'var(--accent-text)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        } : {
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '7px 12px', background: 'var(--accent-dim)',
          border: '1px solid var(--accent-border)', borderRadius: 8,
          color: 'var(--accent-text)', fontSize: 12, fontWeight: 700, cursor: 'pointer',
        }}>
        <Plus style={{ width: layout === 'square' ? 18 : 13, height: layout === 'square' ? 18 : 13 }} />
        {layout === 'inline' && 'Add photo'}
      </button>
    </>
  )
}

// ─── Lightbox (shared overlay) ───────────────────────────────────────────────

// Shared between the staff JobPhotoStrip and the vendor/portal photo
// surfaces. Three consumers, one component. Optional chrome:
//   * onDelete + canDeleteForPhoto → delete button (staff only today)
//   * showVendorAttribution        → "Vendor" pill (staff only today;
//                                    vendor/portal want their own labels
//                                    or no attribution — vendor knows it's
//                                    their own photo; portal client never
//                                    sees vendor identity per the Phase 1
//                                    privacy contract)
//   * signedUrlQueryKey            → cache-share key. Defaults to the
//                                    staff thumbnail key so existing
//                                    behavior is preserved.
export interface LightboxProps {
  photos: JobPhoto[]
  index: number
  onClose: () => void
  onPrev: () => void
  onNext: () => void
  onDelete?: (p: JobPhoto) => void
  canDeleteForPhoto?: (p: JobPhoto) => boolean
  showVendorAttribution?: boolean
  signedUrlQueryKey?: string
}

export function Lightbox({
  photos, index, onClose, onPrev, onNext,
  onDelete, canDeleteForPhoto, showVendorAttribution,
  signedUrlQueryKey,
}: LightboxProps) {
  const photo = photos[index]
  const { data: url } = useSignedThumbUrl(photo.storage_path, signedUrlQueryKey)
  const [confirming, setConfirming] = useState(false)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
      else if (e.key === 'ArrowLeft') onPrev()
      else if (e.key === 'ArrowRight') onNext()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose, onPrev, onNext])

  // Body scroll lock while open — prevents the underlying page from
  // scrolling under a finger drag on iOS Safari when the user releases
  // outside the image area. Restores the prior value on unmount so we
  // never clobber another component's overflow choice.
  useEffect(() => {
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prev }
  }, [])

  // Reset the confirm state when the active photo changes — prevents
  // a stale "Delete?" prompt from carrying over to the next photo.
  useEffect(() => { setConfirming(false) }, [photo.id])

  const deletable = !!onDelete && (canDeleteForPhoto?.(photo) ?? true)
  const showAttributionPill = showVendorAttribution && photo.uploaded_by_vendor_user_id != null
  const hasMultiplePhotos = photos.length > 1

  // Swipe-to-advance state. Threshold pattern (no drag-follow transform —
  // keep it simple): record touch start, on end compute delta, advance if
  // horizontal-dominant past threshold. The axis check (|dx| > |dy|)
  // prevents a vertical drag or pinch from flipping photos accidentally.
  const swipeRef = useRef<{ x: number; y: number } | null>(null)
  const SWIPE_THRESHOLD_PX = 50

  const onTouchStart = (e: ReactTouchEvent<HTMLDivElement>) => {
    if (!hasMultiplePhotos) return
    const t = e.touches[0]
    swipeRef.current = { x: t.clientX, y: t.clientY }
  }
  const onTouchEnd = (e: ReactTouchEvent<HTMLDivElement>) => {
    if (!hasMultiplePhotos) return
    const start = swipeRef.current
    swipeRef.current = null
    if (!start) return
    const t = e.changedTouches[0]
    const dx = t.clientX - start.x
    const dy = t.clientY - start.y
    if (Math.abs(dx) < SWIPE_THRESHOLD_PX) return
    if (Math.abs(dx) <= Math.abs(dy)) return  // axis guard — vertical drag/pinch ignored
    if (dx > 0) onPrev()
    else onNext()
  }

  return (
    <div
      role="dialog" aria-modal="true" aria-label="Photo viewer"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0,
        background: 'var(--overlay-scrim)', backdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 400,
        // Safe-area-aware bottom padding so prev/next chevrons clear the
        // iPhone home indicator on devices that have one.
        padding: '24px 24px calc(24px + env(safe-area-inset-bottom)) 24px',
      }}>
      <div onClick={e => e.stopPropagation()}
        style={{ position: 'relative', maxWidth: 'min(95vw, 1200px)', maxHeight: '90vh', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--ink-secondary)' }}>
            {index + 1} of {photos.length}
            {showAttributionPill && (
              <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 999, background: 'var(--accent-dim)', color: 'var(--accent-text)', border: '1px solid var(--accent-border)' }}>
                Vendor
              </span>
            )}
          </span>
          <div style={{ display: 'flex', gap: 6 }}>
            {deletable && (
              confirming ? (
                <div style={{ display: 'flex', gap: 4 }}>
                  {/* Confirm-delete kept on var(--danger) — saturated red,
                      already high-contrast against the dark scrim. */}
                  <button type="button" onClick={() => onDelete!(photo)}
                    style={{ minHeight: 44, padding: '8px 14px', background: 'var(--danger)', border: 'none', borderRadius: 7, color: 'var(--ink-inverse)', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                    Confirm delete
                  </button>
                  <button type="button" onClick={() => setConfirming(false)}
                    style={{ minHeight: 44, padding: '8px 14px', background: 'var(--on-scrim-fill)', border: '1px solid var(--on-scrim-border)', borderRadius: 7, color: 'var(--on-scrim-ink)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                    Cancel
                  </button>
                </div>
              ) : (
                <button type="button" onClick={() => setConfirming(true)}
                  aria-label="Delete photo"
                  style={{ minWidth: 44, minHeight: 44, padding: 6, background: 'var(--on-scrim-fill)', border: '1px solid var(--on-scrim-border)', borderRadius: 7, color: 'var(--on-scrim-ink)', boxShadow: 'var(--shadow-popover-mid)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Trash2 style={{ width: 14, height: 14 }} />
                </button>
              )
            )}
            <button type="button" onClick={onClose}
              aria-label="Close"
              style={{ minWidth: 44, minHeight: 44, padding: 6, background: 'var(--on-scrim-fill)', border: '1px solid var(--on-scrim-border)', borderRadius: 7, color: 'var(--on-scrim-ink)', boxShadow: 'var(--shadow-popover-mid)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
              <X style={{ width: 14, height: 14 }} />
            </button>
          </div>
        </div>
        {/* Swipe target — touch handlers attached to the image container
            so the photo IS the swipe surface. Vertical drag is ignored
            by the axis guard. Single-photo lightboxes no-op (the start
            handler bails early when hasMultiplePhotos is false).
            Chevrons are absolutely positioned siblings of the <img>,
            placed AFTER the img in DOM order so they paint over it.
            The image container is position:relative — it's their
            containing block. This sidesteps an iOS Safari quirk we hit
            with chevrons positioned against the outer fixed+flex
            overlay (rendered as edge-clipped half-circles); anchoring
            to the relative image container resolves cleanly on iOS. */}
        <div
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
          style={{ position: 'relative', background: 'var(--surface-raised)', borderRadius: 12, overflow: 'hidden', minHeight: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', touchAction: 'pan-y' }}>
          {url ? (
            <img src={url} alt={photo.caption || ''}
              style={{ maxWidth: '100%', maxHeight: '78vh', display: 'block', userSelect: 'none', pointerEvents: 'none' }} />
          ) : (
            <Loader2 style={{ width: 24, height: 24, color: 'var(--ink-tertiary)', animation: 'spin 1s linear infinite' }} />
          )}
          {hasMultiplePhotos && (
            <>
              <button type="button"
                onClick={e => { e.stopPropagation(); onPrev() }}
                aria-label="Previous photo"
                style={{
                  position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)',
                  minWidth: 44, minHeight: 44, padding: 10,
                  background: 'var(--overlay-scrim)', border: 'none',
                  borderRadius: 999, color: 'var(--on-scrim-light-ink)',
                  backdropFilter: 'blur(4px)',
                  boxShadow: 'var(--shadow-popover-mid)',
                  cursor: 'pointer',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                }}>
                <ChevronLeft style={{ width: 24, height: 24 }} />
              </button>
              <button type="button"
                onClick={e => { e.stopPropagation(); onNext() }}
                aria-label="Next photo"
                style={{
                  position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                  minWidth: 44, minHeight: 44, padding: 10,
                  background: 'var(--overlay-scrim)', border: 'none',
                  borderRadius: 999, color: 'var(--on-scrim-light-ink)',
                  backdropFilter: 'blur(4px)',
                  boxShadow: 'var(--shadow-popover-mid)',
                  cursor: 'pointer',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                }}>
                <ChevronRight style={{ width: 24, height: 24 }} />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── Shared internals (upload state, list query, delete handler) ─────────────

function usePhotoController({ jobId, workspaceId }: BaseProps) {
  const queryClient = useQueryClient()
  const profile = useAuth(s => s.profile)
  const isOwnerOrAdmin = !!profile?.is_owner || profile?.role === 'admin'
  const meId = profile?.id ?? null

  const { data: photos = [] } = useQuery({
    queryKey: ['job-photos', jobId],
    queryFn: async () => {
      const { data, error } = await listJobPhotos(jobId)
      if (error) throw error
      return data
    },
    enabled: !!jobId,
  })

  // Vendor name for the staff group header. Cheapest path: one
  // lookup on vendor_assignments — each field_job has at most one
  // accepted vendor assignment at a time, so the vendor name is the
  // same for ALL vendor photos on this job. Runs in parallel with the
  // photo list; null when no accepted assignment exists. Skip the
  // per-photo vendor_users→vendors join entirely.
  const { data: vendorName = null } = useQuery({
    queryKey: ['job-photos-vendor-name', jobId],
    enabled: !!jobId,
    queryFn: async () => {
      const { data } = await supabase
        .from('vendor_assignments')
        .select('vendor:vendors(name)')
        .eq('field_job_id', jobId)
        .eq('status', 'accepted')
        .order('responded_at', { ascending: false })
        .limit(1)
        .maybeSingle()
      // PostgREST returns the embedded to-one as either an object or
      // a one-element array depending on its type-generation heuristic;
      // normalize defensively before pulling .name.
      const v = data?.vendor as unknown
      if (v && typeof v === 'object' && !Array.isArray(v) && typeof (v as { name?: unknown }).name === 'string') return (v as { name: string }).name
      if (Array.isArray(v) && v[0] && typeof v[0].name === 'string') return v[0].name as string
      return null
    },
  })

  const [inFlight, setInFlight] = useState(0)
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)

  const onFiles = async (files: FileList) => {
    const arr = Array.from(files)
    setInFlight(n => n + arr.length)
    await Promise.all(arr.map(async file => {
      // Best-effort client-side WebP transcode (capacity audit §4).
      // Helper never throws — returns the original file on any failure.
      const optimized = await transcodeImageToWebP(file)
      const { error } = await uploadJobPhoto({ workspaceId, jobId, file: optimized })
      if (error) toast.error(`${file.name}: ${error.message}`)
    }))
    setInFlight(n => Math.max(0, n - arr.length))
    queryClient.invalidateQueries({ queryKey: ['job-photos', jobId] })
  }

  const canDeleteForPhoto = (p: JobPhoto) => isOwnerOrAdmin || p.uploaded_by === meId
  const handleDelete = async (p: JobPhoto) => {
    const { error } = await deleteJobPhoto(p)
    if (error) { toast.error(`Delete failed: ${error.message}`); return }
    toast.success('Photo deleted')
    // Drop the lightbox if the deleted photo was the only/last one, else
    // advance to the next visible photo by index clamp on next render.
    setLightboxIndex(idx => (idx == null ? idx : Math.min(idx, photos.length - 2)))
    if (photos.length - 1 <= 0) setLightboxIndex(null)
    queryClient.invalidateQueries({ queryKey: ['job-photos', jobId] })
  }

  // Split into two groups for the staff grouping pattern. Order kept
  // staff-first so the lightbox's linear navigation (index 0..N) walks
  // staff photos before vendor photos. Both groups share the same
  // `photos` array under the hood — index math stays simple.
  // Photos are mutually exclusive by app convention: staff uploads have
  // uploaded_by set; vendor uploads have uploaded_by_vendor_user_id set.
  const staffPhotos  = photos.filter(p => p.uploaded_by_vendor_user_id == null)
  const vendorPhotos = photos.filter(p => p.uploaded_by_vendor_user_id != null)
  const orderedPhotos = [...staffPhotos, ...vendorPhotos]

  return {
    photos: orderedPhotos, staffPhotos, vendorPhotos, vendorName,
    inFlight, onFiles,
    lightboxIndex, setLightboxIndex,
    canDeleteForPhoto, handleDelete,
  }
}

// ─── Public exports ──────────────────────────────────────────────────────────

/**
 * Compact horizontal strip — for JobDrawer. Thumbnails + add button on
 * one row; empty state collapses to the add button + hint.
 */
export function JobPhotoStrip({ jobId, workspaceId }: BaseProps) {
  const ctl = usePhotoController({ jobId, workspaceId })
  const inFlightArr = useMemo(() => Array.from({ length: ctl.inFlight }), [ctl.inFlight])
  const isEmpty = ctl.photos.length === 0 && ctl.inFlight === 0
  // Pixel-equivalence guarantee for the zero-vendor case: when no
  // vendor photos exist, render exactly the legacy single-strip layout
  // — same outer "Photos" header, same flat row of thumbnails, no
  // group sub-headers. The grouped rendering only activates once a
  // vendor photo appears.
  const hasVendor = ctl.vendorPhotos.length > 0
  const vendorLabel = ctl.vendorName ? `Vendor photos · ${ctl.vendorName}` : 'Vendor photos'

  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 12, padding: '12px 14px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: isEmpty ? 0 : 8 }}>
        <p style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: 0 }}>Photos</p>
        <AddPhotoButton size={56} onFiles={ctl.onFiles} layout="inline" />
      </div>
      {isEmpty ? (
        <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: '6px 0 0' }}>
          Add photos — site conditions, equipment, access.
        </p>
      ) : !hasVendor ? (
        // Zero-vendor case: legacy flat row, identical to pre-grouping.
        <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 2 }}>
          {ctl.photos.map((p, i) => (
            <Thumbnail key={p.id} photo={p} size={56} onClick={() => ctl.setLightboxIndex(i)} />
          ))}
          {inFlightArr.map((_, i) => <SkeletonSlot key={`pending-${i}`} size={56} />)}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {ctl.staffPhotos.length > 0 && (
            <div>
              <p style={{ fontSize: 10, fontWeight: 700, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 6px' }}>Job photos</p>
              <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 2 }}>
                {ctl.staffPhotos.map((p, i) => (
                  <Thumbnail key={p.id} photo={p} size={56} onClick={() => ctl.setLightboxIndex(i)} />
                ))}
                {/* Pending uploads always go to the staff group — they
                    haven't picked up uploaded_by_vendor_user_id yet. */}
                {inFlightArr.map((_, i) => <SkeletonSlot key={`pending-${i}`} size={56} />)}
              </div>
            </div>
          )}
          <div>
            <p style={{ fontSize: 10, fontWeight: 700, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 6px' }}>{vendorLabel}</p>
            <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 2 }}>
              {ctl.vendorPhotos.map((p) => {
                // Index in the flat ordered list: staffPhotos come first.
                const flatIndex = ctl.staffPhotos.length + ctl.vendorPhotos.indexOf(p)
                return <Thumbnail key={p.id} photo={p} size={56} onClick={() => ctl.setLightboxIndex(flatIndex)} />
              })}
            </div>
          </div>
        </div>
      )}
      {ctl.lightboxIndex != null && ctl.photos[ctl.lightboxIndex] && (
        <Lightbox
          photos={ctl.photos}
          index={ctl.lightboxIndex}
          onClose={() => ctl.setLightboxIndex(null)}
          onPrev={() => ctl.setLightboxIndex(idx => (idx == null ? null : (idx - 1 + ctl.photos.length) % ctl.photos.length))}
          onNext={() => ctl.setLightboxIndex(idx => (idx == null ? null : (idx + 1) % ctl.photos.length))}
          onDelete={ctl.handleDelete}
          canDeleteForPhoto={ctl.canDeleteForPhoto}
          showVendorAttribution
        />
      )}
    </div>
  )
}

/**
 * Auto-fill grid — for JobDetail. ~120px thumbnails reflow per
 * viewport; empty state shows the add button + hint inline.
 */
export function JobPhotoGrid({ jobId, workspaceId }: BaseProps) {
  const ctl = usePhotoController({ jobId, workspaceId })
  const inFlightArr = useMemo(() => Array.from({ length: ctl.inFlight }), [ctl.inFlight])
  const isEmpty = ctl.photos.length === 0 && ctl.inFlight === 0
  const hasVendor = ctl.vendorPhotos.length > 0
  const vendorLabel = ctl.vendorName ? `Vendor photos · ${ctl.vendorName}` : 'Vendor photos'

  return (
    <div>
      {isEmpty ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 4px' }}>
          <AddPhotoButton size={96} onFiles={ctl.onFiles} layout="inline" />
          <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: 0 }}>
            <ImageIcon style={{ width: 12, height: 12, verticalAlign: '-2px', marginRight: 4 }} />
            Add photos — site conditions, equipment, access.
          </p>
        </div>
      ) : !hasVendor ? (
        // Zero-vendor case: legacy flat grid, identical to pre-grouping.
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 10 }}>
          {ctl.photos.map((p, i) => (
            <Thumbnail key={p.id} photo={p} size={120} onClick={() => ctl.setLightboxIndex(i)} />
          ))}
          {inFlightArr.map((_, i) => <SkeletonSlot key={`pending-${i}`} size={120} />)}
          <AddPhotoButton size={120} onFiles={ctl.onFiles} layout="square" />
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {ctl.staffPhotos.length > 0 && (
            <div>
              <p style={{ fontSize: 10, fontWeight: 700, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 8px' }}>Job photos</p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 10 }}>
                {ctl.staffPhotos.map((p, i) => (
                  <Thumbnail key={p.id} photo={p} size={120} onClick={() => ctl.setLightboxIndex(i)} />
                ))}
                {inFlightArr.map((_, i) => <SkeletonSlot key={`pending-${i}`} size={120} />)}
                <AddPhotoButton size={120} onFiles={ctl.onFiles} layout="square" />
              </div>
            </div>
          )}
          <div>
            <p style={{ fontSize: 10, fontWeight: 700, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 8px' }}>{vendorLabel}</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 10 }}>
              {ctl.vendorPhotos.map((p) => {
                const flatIndex = ctl.staffPhotos.length + ctl.vendorPhotos.indexOf(p)
                return <Thumbnail key={p.id} photo={p} size={120} onClick={() => ctl.setLightboxIndex(flatIndex)} />
              })}
            </div>
          </div>
        </div>
      )}
      {ctl.lightboxIndex != null && ctl.photos[ctl.lightboxIndex] && (
        <Lightbox
          photos={ctl.photos}
          index={ctl.lightboxIndex}
          onClose={() => ctl.setLightboxIndex(null)}
          onPrev={() => ctl.setLightboxIndex(idx => (idx == null ? null : (idx - 1 + ctl.photos.length) % ctl.photos.length))}
          onNext={() => ctl.setLightboxIndex(idx => (idx == null ? null : (idx + 1) % ctl.photos.length))}
          onDelete={ctl.handleDelete}
          canDeleteForPhoto={ctl.canDeleteForPhoto}
          showVendorAttribution
        />
      )}
    </div>
  )
}
